import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Skeleton } from '@/components/shadcn/skeleton'
import { Separator } from '@/components/shadcn/separator'
import type { TimeQuotaModel } from '@/api/model'
import { useGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequota } from '@/api/generated/company-user/company-user'

/** Collapse consecutive calendar weeks with the same weekly hours into ranges. */
function buildWeekRanges(quotas: TimeQuotaModel[]) {
  const currentYear = dayjs().year()
  const sorted = quotas
    .filter((q) => q.year === currentYear)
    .sort((a, b) => (a.year !== b.year ? a.year - b.year : a.calendarWeek - b.calendarWeek))

  if (sorted.length === 0) return []

  const ranges: Array<{ startWeek: number; endWeek: number; year: number; plannedWorkMinutes: number }> = []
  let current = {
    startWeek: sorted[0].calendarWeek,
    endWeek: sorted[0].calendarWeek,
    year: sorted[0].year,
    plannedWorkMinutes: sorted[0].plannedWorkMinutes ?? 0,
  }

  for (const quota of sorted.slice(1)) {
    const sameHours = (quota.plannedWorkMinutes ?? 0) === current.plannedWorkMinutes
    const isNextWeek = quota.year === current.year && quota.calendarWeek === current.endWeek + 1
    if (sameHours && isNextWeek) {
      current.endWeek = quota.calendarWeek
    } else {
      ranges.push(current)
      current = {
        startWeek: quota.calendarWeek,
        endWeek: quota.calendarWeek,
        year: quota.year,
        plannedWorkMinutes: quota.plannedWorkMinutes ?? 0,
      }
    }
  }
  ranges.push(current)
  return ranges
}

const toHours = (minutes: number) => Math.round((minutes / 60) * 100) / 100

interface Props {
  serviceModuleId: number
  employeeId: string
}

export function EmployeeWorkTimeCard({ serviceModuleId, employeeId }: Props) {
  const { t } = useTranslation('employees')
  const currentYear = dayjs().year()

  const { data, isLoading } =
    useGetApiCompaniesCurrentServicemodulesTimetrackingServiceModuleIdTimequota(
      serviceModuleId,
      { employeeId },
      { query: { enabled: !!serviceModuleId && !!employeeId } }
    )

  const ranges = useMemo(() => buildWeekRanges(data?.data ?? []), [data])

  const header = (
    <>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="size-4" />
          {t('detail.quotas.workTime', { year: currentYear })}
        </CardTitle>
      </CardHeader>
      <Separator />
    </>
  )

  if (isLoading) {
    return (
      <Card>
        {header}
        <CardContent className="pt-4">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-48" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Nichts anzuzeigen → Karte komplett ausblenden.
  if (ranges.length === 0) return null

  return (
    <Card>
      {header}
      <CardContent className="pt-4">
        <div className="flex flex-col gap-0.5">
          {ranges.map((range) => (
            <p key={`${range.year}-${range.startWeek}`} className="text-sm">
              {range.startWeek === range.endWeek
                ? t('detail.quotas.week', { week: range.startWeek, year: range.year })
                : t('detail.quotas.weekRange', { start: range.startWeek, end: range.endWeek, year: range.year })}
              : <span className="font-medium">{t('detail.quotas.hoursPerWeek', { hours: toHours(range.plannedWorkMinutes) })}</span>
            </p>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
